import { useMemo } from "react";

import { BugIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { SourceStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import ErrorLayout from "@/layouts/ErrorLayout";

import { SourcesView } from "@/pages/sources/constants";
import SourcesPageBodyGridView from "@/pages/sources/views/SourcesPageBodyGridView";
import SourcesPageBodyTableView from "@/pages/sources/views/SourcesPageBodyTableView";

interface SourcePageBodyProps {
  sources: SourceResource[];
  isLoading: boolean;
  isError: boolean;
  search: string;
  sourceTypes: SourceType[];
  sourceStatuses: SourceStatus[];
  sourceView: SourcesView;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

const SourcesPageBody = ({
  sources,
  isLoading,
  isError,
  search,
  sourceTypes,
  sourceStatuses,
  sourceView,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: SourcePageBodyProps) => {
  const filteredSources = useMemo(() => {
    // Show nothing if no types or statuses are selected
    if (sourceTypes.length === 0 || sourceStatuses.length === 0) {
      return [];
    }

    return sources.filter((source) => {
      const matchesSearch = source.name.toLowerCase().includes(search.toLowerCase());
      const matchesType = sourceTypes.includes(source.sourceType);
      const matchesStatus = sourceStatuses.includes(source.sourceStatus);
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [sources, search, sourceTypes, sourceStatuses]);

  if (isError) {
    return (
      <ErrorLayout icon={BugIcon} header="Error loading sources" message="Please try again later" />
    );
  }

  return match(sourceView)
    .with(SourcesView.LIST, () => (
      <SourcesPageBodyTableView
        sources={filteredSources}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    ))
    .with(SourcesView.GRID, () => (
      <SourcesPageBodyGridView
        sources={filteredSources}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    ))
    .exhaustive();
};

export default SourcesPageBody;
